import React, { useState, useRef, useMemo } from 'react';
import { useRequiredDocuments, useMyDocuments, useUploadDocument } from '../../hooks/useUserQueries';
import './DocumentsTab.css';

const DocumentsTab = ({ user }) => {
  // Fetch required documents from backend
  const { data: requiredDocumentsData, isLoading: isLoadingRequired, refetch: refetchRequired } = useRequiredDocuments();
  // Fetch user's uploaded documents
  const { data: myDocumentsData, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useMyDocuments();
  const uploadDocumentMutation = useUploadDocument();
  
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const fileInputRefs = useRef({});

  // Create a map of uploaded documents by document_type_id
  const uploadedDocumentsMap = useMemo(() => {
    const map = {};
    if (myDocumentsData && Array.isArray(myDocumentsData)) {
      myDocumentsData.forEach(doc => {
        const docTypeId = doc.document_type;
        if (docTypeId) {
          map[docTypeId] = doc;
        }
      });
    }
    return map;
  }, [myDocumentsData]);

  const isLoading = isLoadingRequired || isLoadingDocuments;

  // Get required and optional documents from backend data
  // Backend returns: common documents (is_common=True) + job role specific documents (is_common=False)
  const requiredDocs = useMemo(() => {
    if (!requiredDocumentsData || !Array.isArray(requiredDocumentsData)) return [];
    return requiredDocumentsData.filter(doc => doc.is_required);
  }, [requiredDocumentsData]);

  const optionalDocs = useMemo(() => {
    if (!requiredDocumentsData || !Array.isArray(requiredDocumentsData)) return [];
    return requiredDocumentsData.filter(doc => !doc.is_required);
  }, [requiredDocumentsData]);

  // Separate common documents from job role specific documents
  const commonRequiredDocs = useMemo(() => {
    return requiredDocs.filter(doc => doc.is_common === true);
  }, [requiredDocs]);

  const jobRoleSpecificDocs = useMemo(() => {
    return requiredDocs.filter(doc => doc.is_common === false);
  }, [requiredDocs]);

  // Get user's job role name for display
  const jobRoleName = user?.job_role_detail?.job_role_name || user?.job_role || 'Your Job Role';

  const handleFileSelect = (documentTypeId, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        alert('Only PDF files are allowed.');
        return;
      }
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size cannot exceed 10MB.');
        return;
      }
      handleFileUpload(documentTypeId, file);
    }
  };

  const handleFileUpload = async (documentTypeId, file) => {
    setUploadingDocId(documentTypeId);
    try {
      await uploadDocumentMutation.mutateAsync({
        documents: [{
          document_type_id: documentTypeId,
          file: file,
        }],
      });
      // Refetch documents after successful upload
      refetchRequired();
      refetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingDocId(null);
      // Reset file input
      if (fileInputRefs.current[documentTypeId]) {
        fileInputRefs.current[documentTypeId].value = '';
      }
    }
  };

  const handleCardClick = (documentTypeId) => {
    const existingDoc = uploadedDocumentsMap[documentTypeId];
    if (!existingDoc && !uploadingDocId) {
      fileInputRefs.current[documentTypeId]?.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Check if user has job role (for employees) or is an employer
  const hasJobRole = (user?.role_name === 'employee' && (user?.job_role || user?.job_role_detail)) || user?.role_name === 'employer';

  if (isLoading) {
    return (
      <div className="documents-tab">
        <div className="documents-tab__loading">Loading documents...</div>
      </div>
    );
  }

  // Show message if employee doesn't have job role
  if (user?.role_name === 'employee' && !hasJobRole) {
    return (
      <div className="documents-tab">
        <div className="documents-tab__empty">
          <svg className="documents-tab__empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3>Job Role Required</h3>
          <p>Please complete your profile and select a job role to see required documents.</p>
        </div>
      </div>
    );
  }

  // Calculate progress (all required documents including common and job role specific)
  const uploadedRequiredCount = requiredDocs.filter(doc => uploadedDocumentsMap[doc.id]).length;
  const totalRequiredCount = requiredDocs.length;
  const progressPercentage = totalRequiredCount > 0 ? (uploadedRequiredCount / totalRequiredCount) * 100 : 0;

  return (
    <div className="documents-tab">
      {/* Progress Section */}
      <div className="documents-tab__header">
        <div className="documents-tab__progress">
          <div className="documents-tab__progress-info">
            <h3 className="documents-tab__progress-title">Document Upload Progress</h3>
            <p className="documents-tab__progress-text">
              {uploadedRequiredCount} of {totalRequiredCount} required documents uploaded
            </p>
          </div>
          <div className="documents-tab__progress-bar">
            <div 
              className="documents-tab__progress-fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Common Required Documents Section */}
      {commonRequiredDocs.length > 0 && (
        <div className="documents-tab__section">
          <h3 className="documents-tab__section-title">
            Common Required Documents
            <span className="documents-tab__section-badge">{commonRequiredDocs.length}</span>
          </h3>
          <p className="documents-tab__section-description">
            These documents are required for all employees regardless of job role.
          </p>
          <div className="documents-tab__grid">
            {commonRequiredDocs.map(document => {
              const existingDoc = uploadedDocumentsMap[document.id];
              const isUploading = uploadingDocId === document.id;
              
              return (
                <div
                  key={document.id}
                  className={`documents-tab__card ${existingDoc ? 'documents-tab__card--uploaded' : ''} ${isUploading ? 'documents-tab__card--uploading' : ''}`}
                  onClick={() => !existingDoc && !isUploading && handleCardClick(document.id)}
                >
                  <input
                    ref={el => fileInputRefs.current[document.id] = el}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileSelect(document.id, e)}
                    style={{ display: 'none' }}
                    disabled={isUploading || !!existingDoc}
                  />
                  
                  <div className="documents-tab__card-header">
                    <div className="documents-tab__card-title">
                      {document.name}
                      <span className="documents-tab__required">*</span>
                    </div>
                    <div className="documents-tab__card-status">
                      {existingDoc?.is_verified ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--verified">
                          ✓ Verified
                        </span>
                      ) : existingDoc ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--uploaded">
                          ✓ Uploaded
                        </span>
                      ) : isUploading ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--uploading">
                          ⟳ Uploading...
                        </span>
                      ) : (
                        <span className="documents-tab__status-badge documents-tab__status-badge--empty">
                          + Upload
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="documents-tab__card-body">
                    {existingDoc ? (
                      <div className="documents-tab__file-info">
                        <div className="documents-tab__file-details">
                          <div className="documents-tab__file-name">{existingDoc.document_type_name || document.name}</div>
                          <div className="documents-tab__file-meta">
                            <span>{formatFileSize(existingDoc.file_size)}</span>
                            <span>•</span>
                            <span>Uploaded {formatDate(existingDoc.uploaded_at)}</span>
                          </div>
                        </div>
                        <a
                          href={existingDoc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="documents-tab__view-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View PDF
                        </a>
                      </div>
                    ) : (
                      <div className="documents-tab__upload-hint">
                        <svg className="documents-tab__upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p>Click to upload PDF document</p>
                        <p className="documents-tab__upload-hint-small">Max size: 10MB</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Job Role Specific Documents Section */}
      {jobRoleSpecificDocs.length > 0 && (
        <div className="documents-tab__section">
          <h3 className="documents-tab__section-title">
            {jobRoleName} Specific Documents
            <span className="documents-tab__section-badge">{jobRoleSpecificDocs.length}</span>
          </h3>
          <p className="documents-tab__section-description">
            These documents are specifically required for your job role: <strong>{jobRoleName}</strong>
          </p>
          <div className="documents-tab__grid">
            {jobRoleSpecificDocs.map(document => {
              const existingDoc = uploadedDocumentsMap[document.id];
              const isUploading = uploadingDocId === document.id;
              
              return (
                <div
                  key={document.id}
                  className={`documents-tab__card ${existingDoc ? 'documents-tab__card--uploaded' : ''} ${isUploading ? 'documents-tab__card--uploading' : ''}`}
                  onClick={() => !existingDoc && !isUploading && handleCardClick(document.id)}
                >
                  <input
                    ref={el => fileInputRefs.current[document.id] = el}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileSelect(document.id, e)}
                    style={{ display: 'none' }}
                    disabled={isUploading || !!existingDoc}
                  />
                  
                  <div className="documents-tab__card-header">
                    <div className="documents-tab__card-title">
                      {document.name}
                      <span className="documents-tab__required">*</span>
                    </div>
                    <div className="documents-tab__card-status">
                      {existingDoc?.is_verified ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--verified">
                          ✓ Verified
                        </span>
                      ) : existingDoc ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--uploaded">
                          ✓ Uploaded
                        </span>
                      ) : isUploading ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--uploading">
                          ⟳ Uploading...
                        </span>
                      ) : (
                        <span className="documents-tab__status-badge documents-tab__status-badge--empty">
                          + Upload
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="documents-tab__card-body">
                    {existingDoc ? (
                      <div className="documents-tab__file-info">
                        <div className="documents-tab__file-details">
                          <div className="documents-tab__file-name">{existingDoc.document_type_name || document.name}</div>
                          <div className="documents-tab__file-meta">
                            <span>{formatFileSize(existingDoc.file_size)}</span>
                            <span>•</span>
                            <span>Uploaded {formatDate(existingDoc.uploaded_at)}</span>
                          </div>
                        </div>
                        <a
                          href={existingDoc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="documents-tab__view-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View PDF
                        </a>
                      </div>
                    ) : (
                      <div className="documents-tab__upload-hint">
                        <svg className="documents-tab__upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p>Click to upload PDF document</p>
                        <p className="documents-tab__upload-hint-small">Max size: 10MB</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Optional Documents Section */}
      {optionalDocs.length > 0 && (
        <div className="documents-tab__section">
          <h3 className="documents-tab__section-title">
            Optional Documents
            <span className="documents-tab__section-badge">{optionalDocs.length}</span>
          </h3>
          <p className="documents-tab__section-description">
            Uploading these documents will make more job opportunities available to you.
          </p>
          <div className="documents-tab__grid">
            {optionalDocs.map(document => {
              const existingDoc = uploadedDocumentsMap[document.id];
              const isUploading = uploadingDocId === document.id;
              
              return (
                <div
                  key={document.id}
                  className={`documents-tab__card ${existingDoc ? 'documents-tab__card--uploaded' : ''} ${isUploading ? 'documents-tab__card--uploading' : ''}`}
                  onClick={() => !existingDoc && !isUploading && handleCardClick(document.id)}
                >
                  <input
                    ref={el => fileInputRefs.current[document.id] = el}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileSelect(document.id, e)}
                    style={{ display: 'none' }}
                    disabled={isUploading || !!existingDoc}
                  />
                  
                  <div className="documents-tab__card-header">
                    <div className="documents-tab__card-title">
                      {document.name}
                      {!document.is_common && (
                        <span className="documents-tab__job-role-badge" title={`Required for ${jobRoleName}`}>
                          {jobRoleName}
                        </span>
                      )}
                    </div>
                    <div className="documents-tab__card-status">
                      {existingDoc?.is_verified ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--verified">
                          ✓ Verified
                        </span>
                      ) : existingDoc ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--uploaded">
                          ✓ Uploaded
                        </span>
                      ) : isUploading ? (
                        <span className="documents-tab__status-badge documents-tab__status-badge--uploading">
                          ⟳ Uploading...
                        </span>
                      ) : (
                        <span className="documents-tab__status-badge documents-tab__status-badge--empty">
                          + Upload
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="documents-tab__card-body">
                    {existingDoc ? (
                      <div className="documents-tab__file-info">
                        <div className="documents-tab__file-details">
                          <div className="documents-tab__file-name">{existingDoc.document_type_name || document.name}</div>
                          <div className="documents-tab__file-meta">
                            <span>{formatFileSize(existingDoc.file_size)}</span>
                            <span>•</span>
                            <span>Uploaded {formatDate(existingDoc.uploaded_at)}</span>
                          </div>
                        </div>
                        <a
                          href={existingDoc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="documents-tab__view-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View PDF
                        </a>
                      </div>
                    ) : (
                      <div className="documents-tab__upload-hint">
                        <svg className="documents-tab__upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p>Click to upload PDF document</p>
                        <p className="documents-tab__upload-hint-small">Max size: 10MB</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && requiredDocs.length === 0 && optionalDocs.length === 0 && (
        <div className="documents-tab__empty">
          <svg className="documents-tab__empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3>No Documents Required</h3>
          <p>There are no documents required for your profile at this time.</p>
          {(user?.role_name === 'employee' && user?.job_role_detail) || user?.role_name === 'employer' && (
            <div className="documents-tab__empty-info">
              {user?.role_name === 'employee' && user?.job_role_detail && (
                <p className="documents-tab__empty-info-text">
                  <strong>Your Job Role:</strong> {user.job_role_detail.job_role_name || jobRoleName}
                </p>
              )}
              <p className="documents-tab__empty-info-text">
                If you believe documents should be required for your role, please contact support.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;

